import React, { useState } from 'react';
import { Calculator } from '../../types';
import { CalculatorShell } from './CalculatorShell';
import { exportToPDF, shareToWhatsApp } from '../../utils/exportUtils';
import { AllCalculatorsModule } from './AllCalculatorsModule';

// Import individual calculators
import AgeCalculator from './date/AgeCalculator';
import BusinessDaysCalculator from './date/BusinessDaysCalculator';
import DayCountCalculator from './date/DayCountCalculator';
import DayOfWeekCalculator from './date/DayOfWeekCalculator';
import DaysUntilCalculator from './date/DaysUntilCalculator';

interface TimeModuleProps {
  calculator: Calculator;
  onBack: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}

export const TimeModule: React.FC<TimeModuleProps> = ({
  calculator,
  onBack,
  isFavorite,
  onToggleFavorite,
}) => {
  // Rolar para o topo ao entrar na calculadora
  React.useEffect(() => {
    window.scrollTo(0, 0);
    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [calculator.slug]);

  const [exportData, setExportData] = useState<{ title: string, results: { label: string, value: string }[] } | null>(null);

  const handleExportPDF = () => {
    if (!exportData) return;
    exportToPDF({
      title: exportData.title,
      description: `Relatório gerado em ${new Date().toLocaleDateString('pt-BR')}`,
      results: exportData.results
    });
  };

  const handleShareWhatsApp = () => {
    if (!exportData) return;
    shareToWhatsApp(exportData.title, exportData.results);
  };

  const handleResultChange = React.useCallback((data: { label: string, value: string }[]) => {
    setExportData({ title: calculator.title, results: data });
  }, [calculator.title]);

  const supportedSlugs = [
    'idade',
    'dias-uteis',
    'contagem-de-dias',
    'dia-da-semana',
    'quantos-dias-faltam'
  ];

  if (!supportedSlugs.includes(calculator.slug)) {
    return <AllCalculatorsModule calculator={calculator} onBack={onBack} />;
  }

  const renderTimeCalculator = () => {
    switch (calculator.slug) {
      case 'idade':
        return <AgeCalculator onResultChange={handleResultChange} />;
      case 'dias-uteis':
        return <BusinessDaysCalculator onResultChange={handleResultChange} />;
      case 'contagem-de-dias':
        return <DayCountCalculator onResultChange={handleResultChange} />;
      case 'dia-da-semana':
        return <DayOfWeekCalculator onResultChange={handleResultChange} />;
      case 'quantos-dias-faltam':
        return <DaysUntilCalculator onResultChange={handleResultChange} />;
      default:
        return null;
    }
  };

  return (
    <CalculatorShell
      calculator={calculator}
      onBack={onBack}
      isFavorite={isFavorite}
      onToggleFavorite={onToggleFavorite}
      onExportPDF={exportData ? handleExportPDF : undefined}
      onShareWhatsApp={exportData ? handleShareWhatsApp : undefined}
    >
      {renderTimeCalculator()}
    </CalculatorShell>
  );
};
